import React from 'react'
import { type Control } from 'react-hook-form'
import { Divider, Stack, Typography } from '@mui/material'
import StrategyComponentParameterCard from './strategy-component-parameter-card'
import { type StrategyComponentFormInputs } from './strategy-component-card'
import { type IStrategyComponent, type ParametersRecord } from '../../../models/strategy/strategy-component-types'

import { getProductTypeColour } from '../../../utils/colours-utils'

interface StatefulParametersDisplayProps {
  parameters: ParametersRecord
  processedParameters: Record<string, string | number | boolean>
  strategyName: string
  strategy: IStrategyComponent
  control: Control<StrategyComponentFormInputs>
}

const StatefulParametersDisplay: React.FC<StatefulParametersDisplayProps> = ({
  strategyName, parameters, processedParameters, strategy, control
}) => {
  // States of parameters that require state / form management
  const statesFromParameters = Array.from(new Set(Object.keys(processedParameters)
    .filter(parameter => parameter.includes('.'))
    .map(parameter => parameter.split('.', 1)[0])))

  if (statesFromParameters.length === 0) {
    return null
  } else {
    return (
      <Stack
        direction='row'
        gap={1}
        divider={<Divider orientation='vertical' flexItem/>}
        sx={{ overflowX: 'auto' }}
      >
        {
          statesFromParameters?.map((state, index) => {
            const availableParameters = Object.entries(parameters ?? [])
              .filter(([parameter, value]) => {
                const splits = parameter.split('.', 1)
                return state === splits[0]
              })

            if (availableParameters.length > 0) {
              return (
                <Stack
                  gap={1}
                  key={state}
                  minWidth='150px'
                  paddingBottom={1}
                >
                  <Typography
                    variant='body2' color='primary' fontSize='11px'
                    sx={{ marginBottom: 1, color: getProductTypeColour(state) }}
                  >
                    {state}
                  </Typography>
                  {
                    Object.entries(processedParameters ?? [])
                      .filter(([parameter, value]) => {
                        const splits = parameter.split('.', 1)
                        return state === splits[0]
                      })
                      .map(([parameter, value]) =>
                        <StrategyComponentParameterCard
                          key={`${strategyName}.${parameter}`}
                          name={`${strategyName}.${parameter}`}
                          control={control}
                          strategy={strategy}
                          parameter={parameter}
                          value={value}
                        />
                      )
                  }
                </Stack>
              )
            }
            return null
          })
        }
      </Stack>
    )
  }
}

export default StatefulParametersDisplay
